import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useNotifications } from "@/hooks/useNotifications";
import type { NotificationResponse } from "@/types/user";

// ─── Context Value ───────────────────────────────────────────────────────────

interface NotificationContextValue {
  /** List of recent notifications (most recent first). */
  notifications: NotificationResponse[];
  /** Total unread notification count. */
  unreadCount: number;
  /** Whether the initial fetch is still in progress. */
  isLoading: boolean;
  /** Whether the polling connection is active. */
  isConnected: boolean;
  /** Mark a single notification as read. */
  markAsRead: (notificationId: string) => Promise<void>;
  /** Mark all notifications as read. */
  markAllAsRead: () => Promise<void>;
  /** Add a notification to the local list (e.g. from SSE). */
  addNotification: (notification: NotificationResponse) => void;
  /** Force-refresh notifications from the server. */
  refresh: () => Promise<void>;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined,
);

/**
 * Custom hook to consume the notification context.
 * Must be used inside a `<NotificationProvider>`.
 */
export function useNotificationContext(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error(
      "useNotificationContext must be used within a NotificationProvider",
    );
  }
  return ctx;
}

// ─── Provider ────────────────────────────────────────────────────────────────

interface NotificationProviderProps {
  children: ReactNode;
}

/**
 * Wraps the application and manages notification state globally.
 *
 * Uses the `useNotifications` hook internally for all state management,
 * polling, and API interactions. Exposes the notification state and actions
 * to all consuming components via React context.
 *
 * Only active when the user is authenticated — the underlying hook
 * automatically clears state and stops polling when the user logs out.
 *
 * Add this provider inside `<AuthProvider>` so that it has access to the
 * authentication state.
 */
export function NotificationProvider({ children }: NotificationProviderProps) {
  const {
    notifications,
    unreadCount,
    isLoading,
    isConnected,
    markAsRead,
    markAllAsRead,
    addNotification,
    refresh,
  } = useNotifications();

  const value = useMemo<NotificationContextValue>(
    () => ({
      notifications,
      unreadCount,
      isLoading,
      isConnected,
      markAsRead,
      markAllAsRead,
      addNotification,
      refresh,
    }),
    [
      notifications,
      unreadCount,
      isLoading,
      isConnected,
      markAsRead,
      markAllAsRead,
      addNotification,
      refresh,
    ],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export default NotificationContext;
